import * as Sentry from '@sentry/react';
import { mergeConfig, shouldSkipInit } from '../config';
import type {
  Breadcrumb,
  ErrorContext,
  ObservabilityConfig,
  ObservabilityUser,
  SeverityLevel,
} from '../types';
import { filterError, INIT_IGNORE_ERRORS } from './errorFilters';
import { redactBreadcrumbUrls, redactEventUrls } from './urlRedaction';

let isInitialized = false;

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export function initSentry(userConfig: ObservabilityConfig): boolean {
  const config = mergeConfig(userConfig);
  const skipCheck = shouldSkipInit(config);

  if (skipCheck.skip) {
    console.log(`[Observability] ${skipCheck.reason}`);
    return false;
  }

  if (isInitialized) {
    console.warn('[Observability] Already initialized, skipping');
    return true;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,

      // Performance monitoring
      tracesSampleRate: config.tracesSampleRate,
      profilesSampleRate: config.profilesSampleRate,

      // Integrations
      integrations: [
        // Browser tracing for performance
        Sentry.browserTracingIntegration(),
        // Replay for session recording (optional, can be enabled later)
        // Sentry.replayIntegration(),
      ],

      // Error filtering
      ignoreErrors: INIT_IGNORE_ERRORS,
      beforeSend: (event, hint) => {
        const kept = filterError(event, hint);
        return kept && redactEventUrls(kept);
      },
      beforeSendTransaction: redactEventUrls,
      beforeBreadcrumb: redactBreadcrumbUrls,

      // Debug mode
      debug: config.debug,

      // Don't send PII by default
      sendDefaultPii: false,
    });

    isInitialized = true;
    console.log(`[Observability] Initialized successfully (env: ${config.environment})`);
    return true;
  } catch (error) {
    console.error('[Observability] Failed to initialize Sentry:', error);
    return false;
  }
}

/**
 * Check if Sentry is initialized
 */
export function isSentryInitialized(): boolean {
  return isInitialized;
}

/**
 * Set user context for error tracking
 */
export function setUser(user: ObservabilityUser | null): void {
  if (!isInitialized) return;

  if (user) {
    const { id, email, username, ...rest } = user;
    Sentry.setUser({
      id,
      email,
      username,
      ...rest,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Clear user context
 */
export function clearUser(): void {
  setUser(null);
}

/**
 * Capture an exception to Sentry
 */
export function captureException(error: Error, context?: ErrorContext): string | undefined {
  if (!isInitialized) {
    console.warn('[Observability] Not initialized, error not captured:', error);
    return undefined;
  }

  return Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    user: context?.user,
    level: context?.level,
  });
}

/**
 * Capture a message to Sentry
 */
export function captureMessage(message: string, level: SeverityLevel = 'info'): string | undefined {
  if (!isInitialized) {
    console.log(`[Observability] Not initialized, message not captured: ${message}`);
    return undefined;
  }

  return Sentry.captureMessage(message, level);
}

/**
 * Add a breadcrumb for tracking user actions
 */
export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  if (!isInitialized) return;

  Sentry.addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  });
}

/**
 * Set a tag on all future events
 */
export function setTag(key: string, value: string): void {
  if (!isInitialized) return;
  Sentry.setTag(key, value);
}

/**
 * Set extra context on all future events
 */
export function setExtra(key: string, value: unknown): void {
  if (!isInitialized) return;
  Sentry.setExtra(key, value);
}

/**
 * Set multiple tags at once
 */
export function setTags(tags: Record<string, string>): void {
  if (!isInitialized) return;
  Sentry.setTags(tags);
}

/**
 * Start a new transaction for performance monitoring
 */
export function startTransaction(
  name: string,
  op: string
): ReturnType<typeof Sentry.startInactiveSpan> {
  return Sentry.startInactiveSpan({ name, op });
}

/**
 * Get the current Sentry scope
 */
export function getCurrentScope(): ReturnType<typeof Sentry.getCurrentScope> {
  return Sentry.getCurrentScope();
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
